import { useMemo } from 'react';

import { Scale } from 'lucide-react';

import { PageHeader } from '@/ui/PageHeader';
import { Card, CardContent } from '@/ui/base/card';
import { Badge } from '@/ui/base/badge';
import { Spinner } from '@/ui/Spinner';
import { ErrorState } from '@/ui/ErrorState';
import { EmptyState } from '@/ui/EmptyState';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';

import { CONTEXTO_LABEL, descreverCondicao, useRegrasDeAlcada, type RegraDeAlcada } from './dados';

/**
 * `/admin/parametros/alcadas-genericas` — o motor de alçadas, por fora.
 *
 * Tela só de leitura: mostra as regras de aprovação que a RPC/serviço
 * `avaliar_alcada(contexto, valor)` consulta, agrupadas pelo contexto de
 * negócio em que cada uma se aplica (defesa intempestiva, glosa retroativa,
 * substituição em lote, alteração de demonstrativo já pago). Não existe
 * criação, edição nem exclusão aqui — nem no protótipo de origem havia.
 */
export function RegrasAlcadaGenericasPage() {
  const { data: regras, isLoading, isError, error, refetch } = useRegrasDeAlcada();

  const porContexto = useMemo(() => {
    const agrupado: Record<string, RegraDeAlcada[]> = {};
    for (const regra of regras ?? []) {
      (agrupado[regra.contexto] ??= []).push(regra);
    }
    return agrupado;
  }, [regras]);

  const grupos = Object.entries(porContexto);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Motor de alçadas (genérico)"
        description="Regras de aprovação avaliadas pelo motor de alçadas. Estende o motor existente para defesa intempestiva, glosa retroativa, substituição em lote e alteração de demonstrativo já pago."
      />

      {isLoading ? (
        <div className="p-8 flex justify-center">
          <Spinner />
        </div>
      ) : isError ? (
        <ErrorState
          variant="load-failed"
          description={
            error?.aviso ??
            'Não foi possível buscar as regras de alçada agora. Verifique sua conexão e tente novamente.'
          }
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : grupos.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              icon={Scale}
              title="Nenhuma regra de alçada cadastrada"
              description="Quando houver regra de aprovação configurada, ela aparece aqui, agrupada pelo contexto em que se aplica."
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {grupos.map(([contexto, lista]) => (
            <Card key={contexto}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Scale className="h-4 w-4 text-primary" aria-hidden="true" />
                  <h3 className="font-medium">{CONTEXTO_LABEL[contexto] ?? contexto}</h3>
                  <span className="text-xs text-muted-foreground">({contexto})</span>
                </div>
                <div className="overflow-x-auto">
                  <Table aria-label={`Regras de alçada — ${CONTEXTO_LABEL[contexto] ?? contexto}`}>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ordem</TableHead>
                        <TableHead>Código</TableHead>
                        <TableHead>Regra</TableHead>
                        <TableHead>Condição</TableHead>
                        <TableHead>Aprovador</TableHead>
                        <TableHead>SLA</TableHead>
                        <TableHead>Ativo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lista.map((regra) => (
                        <TableRow key={regra.id}>
                          <TableCell>{regra.ordem}</TableCell>
                          <TableCell className="font-mono text-xs">{regra.codigo}</TableCell>
                          <TableCell>{regra.nome}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {descreverCondicao(regra.condicao)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{regra.papel_aprovador}</Badge>
                          </TableCell>
                          <TableCell>
                            {regra.prazo_aprovacao_dias_uteis
                              ? `${regra.prazo_aprovacao_dias_uteis} dias úteis`
                              : '—'}
                          </TableCell>
                          <TableCell>{regra.ativo ? 'Sim' : 'Não'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default RegrasAlcadaGenericasPage;
